import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useDispatch, useSelector } from 'react-redux'
import { Toaster } from 'sonner'
import App from '@/App'
import { RouterDevtoolsPanel } from '@/components/Layouts/RouterDevtoolsPanel'
import { Logger } from '@/components/Logger'
import { publicConfig } from '@/config/publicConfig'
import useSignOutAndClearStore from '@/hooks/useSignOutAndClearStore'
import { ErrorFallBack } from '@/main'
import { SettingsProvider } from '@/providers/SettingsProvider'
import { ShortcutProvider } from '@/providers/ShortcutProvider'
import type { IRootState } from '@/store'
import { toggleLayout } from '@/store/themeConfigSlice'
import { TOAST_TIMEOUT } from '@/utils/constants'
import { isPromoEntryPath } from '@/utils/promoEntry'

// Secure entry screens must never mount account providers from App or ShortcutProvider.
const SecureEntryLayout = () => {
  const theme = useSelector((state: IRootState) => state.themeConfig.resolvedTheme)
  return (
    <div className="min-h-screen font-primary text-black dark:text-white-dark">
      <ErrorBoundary
        onError={() => Logger.error('Unable to display the secure entry page')}
        fallbackRender={({ resetErrorBoundary }) => (
          <div
            role="alert"
            className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
          >
            <p>Unable to display this page. Please try opening your link again.</p>
            <button type="button" onClick={resetErrorBoundary} className="underline">
              Try again
            </button>
          </div>
        )}
      >
        <Outlet />
      </ErrorBoundary>
      <Toaster theme={theme} duration={TOAST_TIMEOUT} position="bottom-left" />
    </div>
  )
}

const BlankLayout = () => {
  const location = useLocation()
  return isPromoEntryPath(location.pathname) ? <SecureEntryLayout /> : <StandardBlankLayout />
}

const StandardBlankLayout = () => {
  const dispatch = useDispatch()
  const signOutAndClearStore = useSignOutAndClearStore()
  const isError = false

  useEffect(() => {
    dispatch(toggleLayout('full'))
  }, [])

  if (isError) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div className="relative text-center font-semibold ">
          <img
            src="/assets/images/404.svg"
            alt="404"
            className="mx-auto w-full max-w-[630px] object-cover"
          />
          <p className="mt-5 text-base dark:text-white">API is down - try again later!</p>
          <a href={publicConfig.api.baseUrl} className="text-primary visited:text-secondary ...">
            {publicConfig.api.baseUrl}
          </a>

          <Link
            to="/"
            className="font-bold btn btn-gradient mx-auto !mt-7 w-max border-0 uppercase shadow-none"
          >
            Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <App>
      <SettingsProvider>
        <div className="text-black dark:text-white-dark min-h-screen">
          <ErrorBoundary
            onError={(error) => {
              Logger.error('Error in BlankLayout', error)
            }}
            FallbackComponent={ErrorFallBack}
            onReset={() => signOutAndClearStore()}
          >
            <ShortcutProvider>
              <Outlet />
            </ShortcutProvider>
            <RouterDevtoolsPanel />
          </ErrorBoundary>
        </div>
      </SettingsProvider>
    </App>
  )
}

export default BlankLayout
